//src/pages/gro/SurveyCampaignDetailPage.tsx
import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BarChart3,
  CalendarRange,
  CircleHelp,
  Loader2,
  Mail,
  Pencil,
  Power,
  RotateCcw,
  Save,
  Search,
  Send,
  Upload,
  UserPlus,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { listQuestionnaireVersions } from "@/services/questionnaires";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SurveyAudiencePreview } from "@/components/gro/SurveyAudiencePreview";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useAddSurveyCampaignParticipant,
  useCloseSurveyCampaign,
  useDeactivateSurveyCampaignParticipant,
  useLatestSurveyCampaignInviteEmailJob,
  usePublishSurveyCampaign,
  useReactivateSurveyCampaignParticipant,
  useReopenSurveyCampaign,
  useSendSurveyCampaignInviteEmails,
  useSendSurveyCampaignRecipientInviteEmail,
  useSurveyCampaignDetail,
  useUpdateSurveyCampaignRecipientEmailStatus,
  useSurveyCampaignParticipantHistory,
  useUpdateSurveyCampaignSchedule,
} from "@/hooks/useSurveyCampaigns";
import { useQuestionnaires } from "@/hooks/useQuestionnaires";
import { useUnits } from "@/hooks/useUnits";
import { useDepartments } from "@/hooks/useDepartments";
import { useJobPositions } from "@/hooks/useJobPositions";
import { useWorkerGroups } from "@/hooks/useWorkerGroups";
import { useUsers } from "@/hooks/useUsers";
import { useRiskInventories } from "@/hooks/useRiskInventories";
import { useGroSettings } from "@/hooks/useGroSettings";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import type {
  QuestionnaireVersion,
  SurveyCampaignAudiencePreviewItem,
  SurveyCampaignScheduleUpdateIn,
} from "@/types/gro";

const FILTERS_HELP_TEXT =
  "Os filtros possuem dependências. Exemplo: selecionar o cargo Analista e uma Unidade significa buscar usuários com esse cargo nessa unidade. Se não houver ninguém nessa combinação, a campanha não será enviada para ninguém. Grupo de Avaliação é independente dos filtros de unidade, departamento e cargo, podendo ser usado sozinho ou em conjunto. Se um usuário estiver em mais de um filtro, ele deve receber apenas uma pesquisa.";

function statusLabel(status: string) {
  if (status === "draft") return "Rascunho";
  if (status === "published") return "Publicada";
  if (status === "closed") return "Encerrada";
  return "Cancelada";
}

function modeLabel(mode?: string | null) {
  if (mode === "identified") return "Identificada";
  if (mode === "anonymous") return "Anônima";
  if (mode === "pseudonymized") return "Pseudonimizada";
  return "-";
}

function historyActionLabel(action: string) {
  if (action === "manual_add") return "Inclusão manual";
  if (action === "manual_deactivate") return "Desativação";
  if (action === "manual_reactivate") return "Reativação";
  return action;
}

function inviteJobStatusLabel(status?: string | null) {
  if (status === "pending") return "Na fila";
  if (status === "processing") return "Em processamento";
  if (status === "completed") return "Concluído";
  if (status === "completed_with_errors") return "Concluído com falhas";
  if (status === "failed") return "Falhou";
  return "-";
}

function templateModeLabel(mode?: string | null) {
  if (mode === "identified") return "Identificado";
  if (mode === "anonymous") return "Anônimo";
  if (mode === "pseudonymized") return "Pseudoanônimo";
  return "-";
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const parts = new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(d);

  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${map.day}/${map.month}/${map.year} ${map.hour}:${map.minute}`;
}

function formatCampaignDateTime(value?: string | null) {
  if (!value) return "-";

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/,
  );

  if (match) {
    const [, year, month, day, hour, minute] = match;
    return `${day}/${month}/${year} ${hour}:${minute}`;
  }

  return formatDateTime(value);
}

function toCampaignDatetimeLocal(value?: string | null) {
  if (!value) return "";

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/,
  );

  if (match) {
    const [, year, month, day, hour, minute] = match;
    return `${year}-${month}-${day}T${hour}:${minute}`;
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value).slice(0, 16);
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hour = String(d.getHours()).padStart(2, "0");
  const minute = String(d.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function versionLabel(version: QuestionnaireVersion) {
  return version.version_label || `v${version.version_number}`;
}

function formatProgress(value?: number | null) {
  if (typeof value !== "number" || Number.isNaN(value)) return "0,00%";
  return `${value.toFixed(2).replace(".", ",")}%`;
}

type FilterListReadOnlyProps = {
  label: string;
  items: Array<{ id: string; name: string }>;
  selectedIds: string[];
  emptyMessage: string;
};

function FilterListReadOnly({
  label,
  items,
  selectedIds,
  emptyMessage,
}: FilterListReadOnlyProps) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 max-h-[260px] space-y-2 overflow-auto rounded-lg border p-3">
        {items.length === 0 ? (
          <div className="rounded-md border border-dashed bg-muted/30 px-3 py-4 text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          items.map((item) => (
            <label key={item.id} className="flex items-start gap-3 py-1.5">
              <Checkbox checked={selectedIds.includes(item.id)} disabled />
              <div className="text-sm">{item.name}</div>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

export default function SurveyCampaignDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const canWrite =
    user?.permissions?.includes(PERMISSIONS.SURVEY_CAMPAIGNS_WRITE) ?? false;
  const canReopen =
    user?.permissions?.includes(PERMISSIONS.SURVEY_CAMPAIGNS_REOPEN) ?? false;

  const detailQ = useSurveyCampaignDetail(id);
  const groSettingsQ = useGroSettings();

  const publishMut = usePublishSurveyCampaign();
  const closeMut = useCloseSurveyCampaign();
  const reopenMut = useReopenSurveyCampaign();
  const updateScheduleMut = useUpdateSurveyCampaignSchedule();
  const addParticipantMut = useAddSurveyCampaignParticipant();
  const deactivateParticipantMut = useDeactivateSurveyCampaignParticipant();
  const reactivateParticipantMut = useReactivateSurveyCampaignParticipant();
  const latestInviteJobQ = useLatestSurveyCampaignInviteEmailJob(id, !!id);
  const sendInviteEmailsMut = useSendSurveyCampaignInviteEmails();
  const sendRecipientInviteEmailMut = useSendSurveyCampaignRecipientInviteEmail();
  const updateRecipientEmailStatusMut = useUpdateSurveyCampaignRecipientEmailStatus();

  const questionnairesQ = useQuestionnaires({
    include_inactive: false,
    q: "",
  });
  const unitsQ = useUnits();
  const departmentsQ = useDepartments();
  const jobPositionsQ = useJobPositions();
  const workerGroupsQ = useWorkerGroups({
    include_inactive: false,
    q: "",
    group_type: "",
  });
  const inventoriesQ = useRiskInventories({ status: undefined, unit_id: undefined });

  const questionnaireItems = questionnairesQ.data ?? [];
  const units = unitsQ.data ?? [];
  const departments = departmentsQ.data ?? [];
  const jobPositions = jobPositionsQ.data ?? [];
  const workerGroups = workerGroupsQ.data ?? [];
  const inventories = inventoriesQ.data ?? [];

  const unitsMap = useMemo(
    () =>
      Object.fromEntries(
        units.map((item: any) => [item.id, item.name]),
      ) as Record<string, string>,
    [units],
  );

  const departmentsMap = useMemo(
    () =>
      Object.fromEntries(
        departments.map((item: any) => [item.id, item.name]),
      ) as Record<string, string>,
    [departments],
  );

  const jobPositionsMap = useMemo(
    () =>
      Object.fromEntries(
        jobPositions.map((item: any) => [item.id, item.name]),
      ) as Record<string, string>,
    [jobPositions],
  );

  const [questionnaireVersions, setQuestionnaireVersions] = useState<QuestionnaireVersion[]>([]);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<SurveyCampaignScheduleUpdateIn>({
    name: null,
    description: null,
    starts_at: null,
    ends_at: null,
    default_inventory_id: null,
    anonymous_response_tolerance_percent: null,
  });

  const [addParticipantOpen, setAddParticipantOpen] = useState(false);
  const [participantSearch, setParticipantSearch] = useState("");
  const [debouncedParticipantSearch, setDebouncedParticipantSearch] = useState("");
  const [pendingDeactivate, setPendingDeactivate] = useState<SurveyCampaignAudiencePreviewItem | null>(null);
  const [pendingReactivate, setPendingReactivate] = useState<SurveyCampaignAudiencePreviewItem | null>(null);
  const [historyTarget, setHistoryTarget] = useState<SurveyCampaignAudiencePreviewItem | null>(null);
  const [sendInvitesConfirmOpen, setSendInvitesConfirmOpen] = useState(false);

  const usersQ = useUsers({
    q: debouncedParticipantSearch,
    limit: 20,
    offset: 0,
  });

  const historyQ = useSurveyCampaignParticipantHistory(
    id,
    historyTarget?.recipient_id,
    !!historyTarget,
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedParticipantSearch(participantSearch), 350);
    return () => clearTimeout(t);
  }, [participantSearch]);

  useEffect(() => {
    let mounted = true;

    async function loadVersions() {
      if (!questionnaireItems.length) {
        setQuestionnaireVersions([]);
        return;
      }

      const versionGroups = await Promise.all(
        questionnaireItems.map(async (questionnaire) => {
          try {
            return await listQuestionnaireVersions(questionnaire.id);
          } catch {
            return [];
          }
        }),
      );

      if (mounted) {
        setQuestionnaireVersions(versionGroups.flat());
      }
    }

    void loadVersions();

    return () => {
      mounted = false;
    };
  }, [questionnaireItems]);

  const campaign = detailQ.data;
  const isAutoRiskSuggestionMode = groSettingsQ.data?.risk_suggestion_mode === "auto_approve";
  const closeCampaignConfirmDescription = isAutoRiskSuggestionMode
    ? "Ao encerrar a campanha, a coleta de respostas será finalizada, o cálculo final dos analytics será executado automaticamente e os riscos elegíveis serão aprovados e enviados diretamente para o inventário padrão da campanha. Deseja continuar?"
    : "Ao encerrar a campanha, a coleta de respostas será finalizada, o cálculo final dos analytics será executado automaticamente e a análise seguirá para a Fila de Moderação. Deseja continuar?";
  const closeCampaignSuccessMessage = isAutoRiskSuggestionMode
    ? "Campanha encerrada com sucesso. O cálculo final foi executado e os riscos elegíveis foram enviados diretamente para o inventário."
    : "Campanha encerrada com sucesso. O cálculo final foi executado e a análise seguirá pela Fila de Moderação.";

  useEffect(() => {
    if (!campaign) return;
    setScheduleForm({
      name: campaign.name ?? null,
      description: campaign.description ?? null,
      starts_at: campaign.starts_at ?? null,
      ends_at: campaign.ends_at ?? null,
      default_inventory_id: campaign.default_inventory_id ?? null,
      anonymous_response_tolerance_percent:
        campaign.anonymous_mode === "anonymous"
          ? campaign.anonymous_response_tolerance_percent ?? 5
          : null,
    });
  }, [campaign]);

  const currentVersion = useMemo(
    () => questionnaireVersions.find((item) => item.id === campaign?.questionnaire_version_id),
    [campaign?.questionnaire_version_id, questionnaireVersions],
  );

  const questionnaireDisplayName = useMemo(() => {
    if (!campaign) return "";

    const questionnaireName =
      campaign.questionnaire_name ||
      (currentVersion as any)?.questionnaire_name ||
      (currentVersion as any)?.questionnaire_title ||
      (currentVersion as any)?.questionnaire?.name ||
      (currentVersion as any)?.questionnaire?.title ||
      "Questionário";

    const campaignVersionLabel = campaign.questionnaire_version_label
      ? campaign.questionnaire_version_label
      : campaign.questionnaire_version_number
        ? `v${campaign.questionnaire_version_number}`
        : null;

    const label = campaignVersionLabel || (currentVersion ? versionLabel(currentVersion) : null);
    return label ? `${questionnaireName} - ${label}` : questionnaireName;
  }, [campaign, currentVersion]);

  const canManageParticipants = useMemo(() => {
    if (!campaign || !canWrite) return false;
    if (campaign.status !== "draft" && campaign.status !== "published") return false;
    if (campaign.status === "published" && campaign.ends_at) {
      const endsAt = new Date(campaign.ends_at);
      if (!Number.isNaN(endsAt.getTime()) && endsAt < new Date()) return false;
    }
    return true;
  }, [campaign, canWrite]);

  const existingParticipantsByUserId = useMemo(() => {
    const map = new Map<string, SurveyCampaignAudiencePreviewItem>();
    (campaign?.participants_preview || []).forEach((item) => {
      map.set(item.user_id, item);
    });
    return map;
  }, [campaign?.participants_preview]);

  const availableUsers = useMemo(() => {
    const users = usersQ.data ?? [];
    return users.map((u: any) => ({
      ...u,
      existingRecipient: existingParticipantsByUserId.get(u.id),
    }));
  }, [usersQ.data, existingParticipantsByUserId]);

  async function handlePublish() {
    if (!id) return;
    try {
      const result = await publishMut.mutateAsync(id);
      toast.success(`Campanha publicada. Tokens gerados: ${result.tokens_created}.`);
      setPublishConfirmOpen(false);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao publicar campanha.");
    }
  }

  async function handleClose() {
    if (!id) return;
    try {
      await closeMut.mutateAsync(id);
      toast.success(closeCampaignSuccessMessage);
      setCloseConfirmOpen(false);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao encerrar campanha.");
    }
  }

  async function handleReopen() {
    if (!id) return;
    try {
      await reopenMut.mutateAsync(id);
      toast.success("Campanha reativada com sucesso.");
      setReopenConfirmOpen(false);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao reativar campanha.");
    }
  }

  async function handleSaveSchedule() {
    if (!id) return;

    if (
      scheduleForm.starts_at &&
      scheduleForm.ends_at &&
      new Date(scheduleForm.starts_at) > new Date(scheduleForm.ends_at)
    ) {
      toast.error("A data inicial não pode ser maior que a data final.");
      return;
    }

    try {
      await updateScheduleMut.mutateAsync({
        id,
        body: {
          name: scheduleForm.name?.trim() || null,
          description: scheduleForm.description?.trim() || null,
          starts_at: scheduleForm.starts_at || null,
          ends_at: scheduleForm.ends_at || null,
          default_inventory_id: scheduleForm.default_inventory_id || null,
          anonymous_response_tolerance_percent:
            campaign?.anonymous_mode === "anonymous"
              ? (scheduleForm.anonymous_response_tolerance_percent ?? null)
              : null,
        },
      });
      toast.success("Campanha atualizada com sucesso.");
      setScheduleOpen(false);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao atualizar campanha.");
    }
  }

  async function handleAddParticipant(userId: string) {
    if (!id) return;

    try {
      await addParticipantMut.mutateAsync({ campaignId: id, userId });
      toast.success("Participante incluído com sucesso.");
      setAddParticipantOpen(false);
      setParticipantSearch("");
      setDebouncedParticipantSearch("");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao incluir participante.");
    }
  }

  async function handleConfirmDeactivate() {
    if (!id || !pendingDeactivate?.recipient_id) return;

    try {
      await deactivateParticipantMut.mutateAsync({
        campaignId: id,
        recipientId: pendingDeactivate.recipient_id,
      });
      toast.success("Participante desativado com sucesso.");
      setPendingDeactivate(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao desativar participante.");
    }
  }

  async function handleConfirmReactivate() {
    if (!id || !pendingReactivate?.recipient_id) return;

    try {
      await reactivateParticipantMut.mutateAsync({
        campaignId: id,
        recipientId: pendingReactivate.recipient_id,
      });
      toast.success("Participante reativado com sucesso.");
      setPendingReactivate(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao reativar participante.");
    }
  }

  async function handleSendInviteEmails() {
    if (!id) return;
    try {
      const result = await sendInviteEmailsMut.mutateAsync(id);
      toast.success(result.message);
      setSendInvitesConfirmOpen(false);
    } catch (e: any) {
      const message = e?.message || "";
      toast.error(message.includes("Já existe um envio de convites em processamento") ? "Já existe um envio de convites em processamento para esta campanha. Aguarde a conclusão do job atual antes de tentar novamente." : e?.message || "Erro ao iniciar envio dos convites.");
    }
  }

  async function handleResendInviteEmail(item: SurveyCampaignAudiencePreviewItem) {
    if (!id) return;
    try {
      const result = await sendRecipientInviteEmailMut.mutateAsync({
        campaignId: id,
        recipientId: item.recipient_id,
      });
      toast.success(result.message);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao reenviar convite por e-mail.");
    }
  }

  async function handleResetEmailStatus(item: SurveyCampaignAudiencePreviewItem) {
    if (!id) return;
    try {
      const result = await updateRecipientEmailStatusMut.mutateAsync({
        campaignId: id,
        recipientId: item.recipient_id,
        statusSendEmail: "pending",
      });
      toast.success(result.message);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao atualizar o status do e-mail.");
    }
  }

  return (
    <div className="pb-10">
      <div className="sticky top-14 z-20 -mx-1 mb-6 border-b bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/gro/campaigns")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>

            <div>
              <h1 className="text-xl font-bold tracking-tight">Visualizar Campanha</h1>
              <p className="text-sm text-muted-foreground">
                Consulte os dados, acompanhe o andamento e acesse os resultados e a previsão de riscos.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 self-end md:self-auto">
            {campaign?.status === "draft" && canWrite ? (
              <Button variant="outline" onClick={() => navigate(`/gro/campaigns/${id}/editar`)}>
                <Pencil className="mr-2 h-4 w-4" />
                Editar
              </Button>
            ) : null}

            <Button variant="outline" onClick={() => navigate(`/gro/campaigns/${id}/resultados`)}>
              <BarChart3 className="mr-2 h-4 w-4" />
              Resultados e previsão
            </Button>

            {campaign && canWrite && campaign.status === "published" ? (
              <Button
                variant="outline"
                onClick={() => setScheduleOpen(true)}
              >
                <CalendarRange className="mr-2 h-4 w-4" />
                Editar
              </Button>
            ) : null}

            {campaign?.status === "published" && canWrite ? (
              <Button variant="outline" onClick={() => setSendInvitesConfirmOpen(true)} disabled={sendInviteEmailsMut.isPending || latestInviteJobQ.data?.status === "pending" || latestInviteJobQ.data?.status === "processing"}>
                <Send className="mr-2 h-4 w-4" />
                {latestInviteJobQ.data?.status === "pending" || latestInviteJobQ.data?.status === "processing" ? "Envio em processamento" : "Enviar convites"}
              </Button>
            ) : null}

            {campaign?.status === "draft" && canWrite ? (
              <Button onClick={() => setPublishConfirmOpen(true)} disabled={publishMut.isPending}>
                <Upload className="mr-2 h-4 w-4" />
                Publicar
              </Button>
            ) : null}

            {campaign?.status === "published" && canWrite ? (
              <Button variant="outline" onClick={() => setCloseConfirmOpen(true)} disabled={closeMut.isPending}>
                <Power className="mr-2 h-4 w-4" />
                Encerrar
              </Button>
            ) : null}

            {campaign?.status === "closed" && canReopen ? (
              <Button variant="outline" onClick={() => setReopenConfirmOpen(true)} disabled={reopenMut.isPending}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Reativar campanha
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {detailQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando campanha...
        </Card>
      ) : detailQ.isError || !campaign ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(detailQ.error as any)?.message || "Erro ao carregar campanha"}
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant={
                    campaign.status === "published"
                      ? "default"
                      : campaign.status === "draft"
                      ? "secondary"
                      : "outline"
                  }
                >
                  {statusLabel(campaign.status)}
                </Badge>

                <Badge variant="outline">
                  {campaign.participants_count} participante(s)
                </Badge>

                <Badge variant="outline">
                  {campaign.responded_count} responderam
                </Badge>

                <Badge variant="outline">
                  {formatProgress(campaign.progress_percentage)} de avanço
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Nome</Label>
                <Input className="mt-1" value={campaign.name} disabled />
              </div>

              <div>
                <Label>Modo</Label>
                <Select value={campaign.anonymous_mode} disabled>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={modeLabel(campaign.anonymous_mode)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={campaign.anonymous_mode}>
                      {modeLabel(campaign.anonymous_mode)}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label>Descrição</Label>
                <textarea
                  className="mt-1 flex min-h-[120px] w-full rounded-md border bg-muted/40 px-3 py-2 text-sm"
                  value={campaign.description || ""}
                  disabled
                />
              </div>

              <div>
                <Label>Questionário</Label>
                <Select value={campaign.questionnaire_version_id} disabled>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={questionnaireDisplayName || campaign.questionnaire_version_id} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={campaign.questionnaire_version_id}>
                      {questionnaireDisplayName || campaign.questionnaire_version_id}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Inventário padrão</Label>
                <Input
                  className="mt-1"
                  value={
                    (campaign as any).default_inventory_title ||
                    (campaign as any).default_inventory_id ||
                    "-"
                  }
                  disabled
                />
              </div>

              {campaign.anonymous_mode === "anonymous" ? (
                <>
                  <div>
                    <Label>Tolerância de respostas (%)</Label>
                    <Input
                      className="mt-1"
                      value={
                        campaign.anonymous_response_tolerance_percent != null
                          ? String(campaign.anonymous_response_tolerance_percent)
                          : "-"
                      }
                      disabled
                    />
                  </div>

                  <div>
                    <Label>Bloqueio por limite anônimo</Label>
                    <Input
                      className="mt-1"
                      value={formatDateTime(campaign.anonymous_response_limit_reached_at)}
                      disabled
                    />
                  </div>
                </>
              ) : null}

              <div>
                <Label>Início</Label>
                <Input
                  className="mt-1"
                  value={formatCampaignDateTime(campaign.starts_at)}
                  disabled
                />
              </div>

              <div>
                <Label>Fim</Label>
                <Input
                  className="mt-1"
                  value={formatCampaignDateTime(campaign.ends_at)}
                  disabled
                />
              </div>

              <div>
                <Label>Publicado em</Label>
                <Input className="mt-1" value={formatDateTime(campaign.published_at)} disabled />
              </div>

              <div>
                <Label>Encerrado em</Label>
                <Input className="mt-1" value={formatDateTime(campaign.closed_at)} disabled />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Convites por e-mail
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border bg-muted/20 p-4">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Configuração</div>
                <div className="mt-2 text-sm font-medium">SMTP no cadastro da empresa</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Configure o servidor SMTP em Cadastros &gt; Empresa para habilitar os disparos desta campanha.
                </div>
              </div>

              <div className="rounded-xl border bg-muted/20 p-4">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Template</div>
                <div className="mt-2 text-sm font-medium">{templateModeLabel(campaign.anonymous_mode)}</div>
                <div className="mt-1 text-xs text-muted-foreground">Um template específico será usado conforme o modo da campanha.</div>
              </div>

              <div className="rounded-xl border bg-muted/20 p-4">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Último envio</div>
                <div className="mt-2 text-sm font-medium">{inviteJobStatusLabel(latestInviteJobQ.data?.status)}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {latestInviteJobQ.data?.requested_at ? `Solicitado em ${formatDateTime(latestInviteJobQ.data.requested_at)}` : "Nenhum disparo registrado ainda."}
                </div>
              </div>

              <div className="rounded-xl border bg-muted/20 p-4">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Resumo</div>
                <div className="mt-2 text-sm font-medium">
                  {latestInviteJobQ.data ? `${latestInviteJobQ.data.total_sent} enviado(s) / ${latestInviteJobQ.data.total_failed} falha(s)` : `${campaign.participants_count} participante(s)`}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {latestInviteJobQ.data ? `${latestInviteJobQ.data.total_pending} pendente(s) e ${latestInviteJobQ.data.total_processing} em processamento.` : "O envio usa endereço no-reply e inclui rodapé Powered by Normex."}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Filtros do público-alvo
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
                      aria-label="Ajuda sobre os filtros do público-alvo"
                    >
                      <CircleHelp className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[360px] text-sm leading-5">
                    {FILTERS_HELP_TEXT}
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
            </CardHeader>

            <CardContent className="grid grid-cols-1 gap-4 lg:grid-cols-4">
              <FilterListReadOnly
                label="Unidades"
                items={units}
                selectedIds={campaign.filters.unit_ids || []}
                emptyMessage="Nenhuma unidade encontrada."
              />

              <FilterListReadOnly
                label="Departamentos"
                items={departments}
                selectedIds={campaign.filters.department_ids || []}
                emptyMessage="Nenhum departamento encontrado."
              />

              <FilterListReadOnly
                label="Cargos"
                items={jobPositions}
                selectedIds={campaign.filters.job_position_ids || []}
                emptyMessage="Nenhum cargo encontrado."
              />

              <FilterListReadOnly
                label="Grupos de avaliação"
                items={workerGroups}
                selectedIds={campaign.filters.worker_group_ids || []}
                emptyMessage="Nenhum grupo de avaliação encontrado."
              />
            </CardContent>
          </Card>

          <SurveyAudiencePreview
            items={campaign.participants_preview}
            unitsMap={unitsMap}
            departmentsMap={departmentsMap}
            jobPositionsMap={jobPositionsMap}
            showSensitiveColumns={campaign.anonymous_mode === "identified"}
            allowCopySharedLink={campaign.anonymous_mode === "anonymous"}
            sharedSurveyToken={campaign.anonymous_mode === "anonymous" ? campaign.anonymous_public_token : null}
            canManageParticipants={canManageParticipants}
            canResendEmail={canWrite}
            onAddParticipant={() => setAddParticipantOpen(true)}
            onDeactivateParticipant={setPendingDeactivate}
            onReactivateParticipant={setPendingReactivate}
            onOpenHistory={setHistoryTarget}
            onResendEmail={handleResendInviteEmail}
            onResetEmailStatus={handleResetEmailStatus}
          />
        </div>
      )}


      <ConfirmDialog
        open={sendInvitesConfirmOpen}
        onOpenChange={setSendInvitesConfirmOpen}
        title="Enviar convites por e-mail"
        description={`Os convites serão enviados em segundo plano para os participantes elegíveis da campanha usando o template ${templateModeLabel(campaign?.anonymous_mode)}. Este envio utiliza endereço no-reply e orienta o respondente a não responder o e-mail.`}
        onConfirm={handleSendInviteEmails}
        confirmLabel="Iniciar envio"
      />

      <ConfirmDialog
        open={publishConfirmOpen}
        onOpenChange={setPublishConfirmOpen}
        title="Publicar Campanha"
        description="Deseja publicar esta campanha agora? Após a publicação, a pesquisa ficará disponível para os participantes elegíveis."
        onConfirm={handlePublish}
        confirmLabel="Publicar"
      />

      <ConfirmDialog
        open={closeConfirmOpen}
        onOpenChange={setCloseConfirmOpen}
        title="Encerrar Campanha"
        description={closeCampaignConfirmDescription}
        onConfirm={handleClose}
        confirmLabel="Encerrar campanha"
        variant="destructive"
      />

      <ConfirmDialog
        open={reopenConfirmOpen}
        onOpenChange={setReopenConfirmOpen}
        title="Reativar Campanha"
        description="Ao reativar a campanha, os riscos sugeridos desta campanha serão removidos da Fila de Moderação e os itens gerados no inventário também serão removidos. Se algum item do inventário gerado por esta campanha já tiver plano de ação vinculado, a reativação será bloqueada. Deseja continuar?"
        onConfirm={handleReopen}
        confirmLabel="Reativar campanha"
      />

      <ConfirmDialog
        open={!!pendingDeactivate}
        onOpenChange={(open) => {
          if (!open) setPendingDeactivate(null);
        }}
        title="Desativar participante"
        description={
          pendingDeactivate
            ? `Deseja desativar ${pendingDeactivate.name || pendingDeactivate.email || "este participante"}? Ele não poderá mais responder a pesquisa com o token atual enquanto estiver desativado.`
            : ""
        }
        onConfirm={handleConfirmDeactivate}
        confirmLabel="Desativar"
        variant="destructive"
      />

      <ConfirmDialog
        open={!!pendingReactivate}
        onOpenChange={(open) => {
          if (!open) setPendingReactivate(null);
        }}
        title="Reativar participante"
        description={
          pendingReactivate
            ? `Deseja reativar ${pendingReactivate.name || pendingReactivate.email || "este participante"}? O mesmo token voltará a poder ser utilizado.`
            : ""
        }
        onConfirm={handleConfirmReactivate}
        confirmLabel="Reativar"
      />

      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar campanha</DialogTitle>
            <DialogDescription>
              Edite os campos permitidos para campanhas publicadas e ainda não encerradas.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>Nome</Label>
              <Input
                className="mt-1"
                value={scheduleForm.name ?? ""}
                onChange={(e) =>
                  setScheduleForm((current) => ({
                    ...current,
                    name: e.target.value,
                  }))
                }
              />
            </div>

            <div className="md:col-span-2">
              <Label>Descrição</Label>
              <textarea
                className="mt-1 flex min-h-[96px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={scheduleForm.description ?? ""}
                onChange={(e) =>
                  setScheduleForm((current) => ({
                    ...current,
                    description: e.target.value,
                  }))
                }
              />
            </div>

            {campaign?.anonymous_mode === "anonymous" ? (
              <div>
                <Label>Tolerância de respostas (%)</Label>
                <Input
                  className="mt-1"
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={scheduleForm.anonymous_response_tolerance_percent ?? ""}
                  onChange={(e) =>
                    setScheduleForm((current) => ({
                      ...current,
                      anonymous_response_tolerance_percent:
                        e.target.value === "" ? null : Number(e.target.value),
                    }))
                  }
                />
              </div>
            ) : null}

            {campaign?.anonymous_mode === "anonymous" ? (
              <div>
                <Label>Bloqueio por limite anônimo</Label>
                <Input
                  className="mt-1"
                  value={formatDateTime(campaign.anonymous_response_limit_reached_at)}
                  disabled
                />
              </div>
            ) : null}

            <div className={campaign?.anonymous_mode === "anonymous" ? "md:col-span-2" : ""}>
              <Label>Inventário padrão</Label>
              <Select
                value={scheduleForm.default_inventory_id || "__none__"}
                onValueChange={(value) =>
                  setScheduleForm((current) => ({
                    ...current,
                    default_inventory_id: value === "__none__" ? null : value,
                  }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Nenhum inventário padrão" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nenhum inventário padrão</SelectItem>
                  {inventories.map((inventory: any) => (
                    <SelectItem key={inventory.id} value={inventory.id}>
                      {inventory.name || inventory.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Nova data de início</Label>
              <Input
                className="mt-1"
                type="datetime-local"
                value={toCampaignDatetimeLocal(scheduleForm.starts_at)}
                onChange={(e) =>
                  setScheduleForm((current) => ({
                    ...current,
                    starts_at: e.target.value || null,
                  }))
                }
              />
            </div>

            <div>
              <Label>Nova data de término</Label>
              <Input
                className="mt-1"
                type="datetime-local"
                value={toCampaignDatetimeLocal(scheduleForm.ends_at)}
                onChange={(e) =>
                  setScheduleForm((current) => ({
                    ...current,
                    ends_at: e.target.value || null,
                  }))
                }
              />
            </div>

            <div className="md:col-span-2 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setScheduleOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveSchedule} disabled={updateScheduleMut.isPending}>
                Salvar alterações
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addParticipantOpen} onOpenChange={setAddParticipantOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Adicionar participante</DialogTitle>
            <DialogDescription>
              Pesquise um usuário ativo e inclua manualmente nesta campanha.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou e-mail..."
                value={participantSearch}
                onChange={(e) => setParticipantSearch(e.target.value)}
                className="pl-8"
              />
            </div>

            <div className="max-h-[380px] overflow-auto rounded-lg border">
              {usersQ.isLoading ? (
                <div className="p-4 text-sm text-muted-foreground">Carregando usuários...</div>
              ) : availableUsers.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">
                  Nenhum usuário encontrado.
                </div>
              ) : (
                <div className="divide-y">
                  {availableUsers.map((item: any) => {
                    const existingRecipient = item.existingRecipient;
                    const alreadyActive =
                      existingRecipient && existingRecipient.delivery_status !== "cancelled";

                    return (
                      <div
                        key={item.id}
                        className="flex items-center justify-between gap-3 p-4"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{item.name}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {item.email || "-"}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {alreadyActive ? (
                            <Badge variant="outline">Já participante</Badge>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleAddParticipant(item.id)}
                              disabled={addParticipantMut.isPending}
                            >
                              <UserPlus className="mr-2 h-4 w-4" />
                              Adicionar
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!historyTarget}
        onOpenChange={(open) => {
          if (!open) setHistoryTarget(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Histórico do participante</DialogTitle>
            <DialogDescription>
              {historyTarget?.name || historyTarget?.email || "Participante"}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[420px] overflow-auto rounded-lg border">
            {historyQ.isLoading ? (
              <div className="p-4 text-sm text-muted-foreground">Carregando histórico...</div>
            ) : historyQ.data && historyQ.data.length > 0 ? (
              <div className="divide-y">
                {historyQ.data.map((event) => (
                  <div key={event.id} className="p-4">
                    <div className="text-sm font-medium">
                      {historyActionLabel(event.action)}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {event.created_by_name || event.created_by_email || event.created_by}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {formatDateTime(event.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 text-sm text-muted-foreground">
                Nenhum histórico encontrado para este participante.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
